/**
 * Helpers for the XPEN-2524 auto-calculation toggle on the Aderant
 * additional-fields modal. Decoupled from the modal so the hint copy and
 * the URL contract can be unit-tested without driving the form.
 */

/**
 * Build the query string for GET /aderant/disbursement_rate/.
 *
 * Returns null when the inputs aren't sufficient to issue the call; the
 * caller skips the fetch instead of firing a request that the backend will
 * just reject as `matter_not_found`.
 */
export function buildRateLookupUrl({ matter_id, cost_type, quantity }) {
    if (!matter_id || !cost_type) return null;
    const params = new URLSearchParams({
        matter_id: String(matter_id),
        cost_type: String(cost_type),
        quantity: quantity == null || quantity === "" ? "" : String(quantity),
    });
    return `/aderant/disbursement_rate/?${params.toString()}`;
}

/**
 * Translate the backend's `{rate_source, reason}` response into a short
 * user-facing hint. Returns null when there's nothing useful to show
 * (e.g. still waiting for the first response).
 */
export function formatRateSourceHint({ rate_source, reason } = {}, readLabel) {
    if (rate_source === 'matter') return readLabel('invoice.rate_source_matter', 'Source: matter rate');
    if (rate_source === 'client') return readLabel('invoice.rate_source_client', 'Source: client rate');
    if (rate_source === 'firm') return readLabel('invoice.rate_source_firm', 'Source: firm rate');
    if (reason === 'no_rate_available') {
        return readLabel('invoice.rate_source_none', 'No firm rate configured — enter manually');
    }
    if (reason === 'matter_not_found') {
        return readLabel('invoice.rate_source_matter_missing', 'Matter not found — enter manually');
    }
    if (reason === 'invalid_date') {
        return readLabel('invoice.rate_source_invalid_date', 'Invalid date — enter manually');
    }
    return null;
}

/**
 * The toggle should LOCK the field only when a real rate resolved. When
 * the toggle is on but no rate is available, the user falls back to
 * manual entry — the input must stay editable so they can type.
 */
export function shouldLockToBillForAutoCalc(autoCalcOn, autoCalcInfo) {
    if (!autoCalcOn) return false;
    return Boolean(autoCalcInfo?.rate_source);
}
